import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { formatApiError } from "../lib/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import GoogleSignInButton from "../components/GoogleSignInButton";

const COAT = "https://customer-assets.emergentagent.com/job_66cda0d4-d07a-4a92-8bdd-b78d3aba21d7/artifacts/xbhowqoo_ephigie-hayti.jpg";

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const tierParam = params.get("tier");

  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", country: "HT", membership: "non_member",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      await register(form);
      nav(tierParam ? `/enroll?tier=${tierParam}` : "/student");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="register-page">
      <Header />
      <div className="max-w-xl mx-auto px-5 py-20">
        <div className="text-center mb-10">
          <img src={COAT} alt="" className="h-16 w-16 mx-auto object-contain mix-blend-screen mb-4" />
          <h1 className="font-serif text-4xl">{t("auth.register")}</h1>
          <p className="text-neutral-400 mt-2 text-sm">{t("auth.registerSub")}</p>
        </div>
        <div className="card-imperial p-8 mb-4" data-testid="register-google-card">
          <GoogleSignInButton
            next={tierParam ? `/enroll?tier=${tierParam}` : "/student"}
            testId="register-google-btn"
          />
          <div className="mt-5 flex items-center gap-3 text-[10px] text-neutral-500 uppercase tracking-[0.2em]">
            <div className="flex-1 h-px bg-[#1a1a1a]" />
            <span>{t("auth.orDivider", "or")}</span>
            <div className="flex-1 h-px bg-[#1a1a1a]" />
          </div>
        </div>
        <form onSubmit={onSubmit} className="card-imperial p-8 grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="register-form">
          <Field label={t("auth.name")} value={form.name} onChange={set("name")} testId="register-name" required full />
          <Field label={t("auth.email")} type="email" value={form.email} onChange={set("email")} testId="register-email" required />
          <Field label={t("auth.password")} type="password" value={form.password} onChange={set("password")} testId="register-password" required />
          <Field label={t("auth.phone")} value={form.phone} onChange={set("phone")} testId="register-phone" />
          <Field label={t("auth.country")} value={form.country} onChange={set("country")} testId="register-country" />
          <div className="md:col-span-2">
            <div className="overline mb-2">{t("auth.membership")}</div>
            <div className="flex gap-3">
              {[
                { v: "member", l: t("auth.member") },
                { v: "non_member", l: t("auth.nonMember") },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`register-membership-${o.v}`}
                  onClick={() => setForm((f) => ({ ...f, membership: o.v }))}
                  className={`flex-1 px-4 py-2.5 text-sm font-condensed tracking-widest uppercase border transition-colors ${
                    form.membership === o.v ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/5" : "border-[#222] text-neutral-400 hover:border-[#444]"
                  }`}
                >
                  {o.l}
                </button>
              ))}
            </div>
          </div>
          {err && <div data-testid="register-error" className="md:col-span-2 text-sm text-[#e6192b]">{err}</div>}
          <button type="submit" disabled={busy} data-testid="register-submit" className="btn-imperial md:col-span-2 w-full">
            {busy ? t("common.loading") : t("auth.submitRegister")}
          </button>
          <div className="md:col-span-2 text-sm text-neutral-500 text-center pt-2">
            {t("auth.haveAccount")}{" "}
            <Link to="/login" data-testid="register-to-login" className="text-[#d4af37] hover:underline">
              {t("auth.submitLogin")}
            </Link>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, type = "text", value, onChange, testId, required, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="overline mb-2">{label}</div>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2.5 text-sm transition-colors"
      />
    </label>
  );
}
